import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SubAccountLayout from '../../components/SubAccountLayout';
import { useSubAccount } from '../../components/SubAccountLayout';
import { useTheme } from '../../context/ThemeContext';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from 'date-fns';
import { toast } from 'sonner';
import {
  PhoneOutgoing, PhoneIncoming, Phone, Calendar as CalendarIcon, Bot, MessageSquare,
  TrendingUp, ArrowUpRight, Activity, CheckCircle, Sparkles, Mic, BarChart3,
  Headphones, ArrowRight, CalendarDays, PhoneMissed, Percent, Timer, Crown,
  Users, ChevronDown, Loader2, AlertTriangle, RotateCcw, Download, FileText,
  FileSpreadsheet, X, PhoneCall, Send, DollarSign, Target, GripVertical,
  SlidersHorizontal, ChevronLeft, ChevronRight, ArrowUpToLine
} from 'lucide-react';

// Clean line chart — thin strokes, hover tooltip, pointy peaks (Y-buffer), overlap-offset for equal values
const LineChart = ({ data, lines, height = 220, isDarkMode }) => {
  const textSecondary = isDarkMode ? '#9ca3af' : '#6b7280';
  const [hoverIdx, setHoverIdx] = React.useState(null);
  if (!data || data.length === 0) return null;

  const allValues = lines.flatMap(l => data.map(d => d[l.key] || 0));
  const rawMax = Math.max(...allValues, 1);
  // 20% headroom so the top peak never touches the ceiling (kills "square top" look)
  const maxVal = rawMax + Math.max(1, Math.ceil(rawMax * 0.2));
  const pad = { top: 16, right: 12, bottom: 28, left: 32 };
  const chartW = 700, chartH = height;
  const innerW = chartW - pad.left - pad.right;
  const innerH = chartH - pad.top - pad.bottom;
  const getX = (i) => pad.left + (i / Math.max(data.length - 1, 1)) * innerW;
  const getY = (val) => pad.top + innerH - (val / maxVal) * innerH;
  // Tiny per-series Y offset so identical values (e.g. Calls=Answered=3) stay visually distinct
  const seriesOffset = (j) => (j - (lines.length - 1) / 2) * 1.2;
  const buildPath = (key, j) => data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d[key] || 0) + seriesOffset(j)}`).join(' ');

  const yTicks = Math.min(4, Math.max(2, rawMax));
  const gridLines = Array.from({ length: yTicks + 1 }, (_, i) => {
    const v = Math.round((rawMax / yTicks) * i);
    return { val: v, y: getY(v) };
  });

  // Hit-zones for hover tooltip
  const stepW = innerW / Math.max(data.length - 1, 1);

  return (
    <div className="relative w-full">
      <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full block" preserveAspectRatio="xMidYMid meet">
        <defs>
          {lines.map(l => (
            <linearGradient key={`grad-${l.key}`} id={`grad-${l.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={l.color} stopOpacity="0.22" />
              <stop offset="60%" stopColor={l.color} stopOpacity="0.05" />
              <stop offset="100%" stopColor={l.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>

        {/* Subtle horizontal grid */}
        {gridLines.map(({ val, y }) => (
          <g key={val}>
            <line x1={pad.left} y1={y} x2={chartW - pad.right} y2={y}
              stroke={isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
              strokeWidth="1" strokeDasharray="3 5" />
            <text x={pad.left - 6} y={y + 3} textAnchor="end" fill={textSecondary} fontSize="9" opacity="0.55">{val}</text>
          </g>
        ))}

        {/* Hover vertical guideline */}
        {hoverIdx !== null && (
          <line x1={getX(hoverIdx)} y1={pad.top} x2={getX(hoverIdx)} y2={pad.top + innerH}
            stroke={isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)'} strokeWidth="1" strokeDasharray="2 3" />
        )}

        {/* Soft gradient glow under each line */}
        {lines.map((l, j) => {
          const path = buildPath(l.key, j);
          const area = `${path} L ${getX(data.length - 1)} ${pad.top + innerH} L ${getX(0)} ${pad.top + innerH} Z`;
          return <path key={`area-${l.key}`} d={area} fill={`url(#grad-${l.key})`} />;
        })}

        {/* Thin clean lines — no sparse dots */}
        {lines.map((l, j) => (
          <path key={l.key} d={buildPath(l.key, j)} fill="none" stroke={l.color}
            strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" opacity="0.95" />
        ))}

        {/* Only render dot on the hovered point per series — keeps chart clean */}
        {hoverIdx !== null && lines.map((l, j) => (
          <circle key={`hd-${l.key}`} cx={getX(hoverIdx)} cy={getY(data[hoverIdx][l.key] || 0) + seriesOffset(j)}
            r="3.5" fill={l.color} stroke={isDarkMode ? '#0c0d12' : '#fff'} strokeWidth="1.5" />
        ))}

        {/* X-axis labels */}
        {data.map((d, i) => {
          if (data.length > 14 && i % 3 !== 0) return null;
          if (data.length > 7 && data.length <= 14 && i % 2 !== 0) return null;
          return <text key={i} x={getX(i)} y={chartH - 4} textAnchor="middle" fill={textSecondary} fontSize="9" opacity="0.6">{d.date?.slice(5) || ''}</text>;
        })}

        {/* Invisible hit-zones for hover */}
        {data.map((d, i) => (
          <rect key={`hz-${i}`} x={getX(i) - stepW / 2} y={pad.top} width={stepW} height={innerH}
            fill="transparent" style={{ cursor: 'crosshair' }}
            onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx((v) => (v === i ? null : v))} />
        ))}
      </svg>

      {/* Floating tooltip */}
      {hoverIdx !== null && (() => {
        const d = data[hoverIdx];
        // position tooltip relative to data point inside the svg container
        const leftPct = (getX(hoverIdx) / chartW) * 100;
        const flip = leftPct > 70;
        return (
          <div
            className={`absolute pointer-events-none px-3 py-2 rounded-xl border text-xs shadow-xl ${
              isDarkMode ? 'bg-[#0c0d12]/95 border-white/10 text-gray-100' : 'bg-white/95 border-gray-200 text-gray-900'
            }`}
            style={{ left: `${flip ? leftPct - 12 : leftPct + 2}%`, top: 8, transform: flip ? 'translateX(-100%)' : 'none', minWidth: 150 }}
            data-testid="chart-tooltip"
          >
            <div className={`font-semibold mb-1.5 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{d.date}</div>
            {lines.map(l => (
              <div key={l.key} className="flex items-center justify-between gap-3 py-0.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: l.color }} />
                  <span className="opacity-80">{l.label}</span>
                </div>
                <span className="font-semibold tabular-nums">{d[l.key] || 0}</span>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
};

// ─── Enterprise Funnel Pipeline ───
const FunnelShape = ({ stages, colorScheme = 'violet', isDarkMode }) => {
  const schemes = {
    violet: {
      barFrom: isDarkMode ? 'rgba(139, 92, 246, 0.35)' : 'rgba(139, 92, 246, 0.18)',
      barTo: isDarkMode ? 'rgba(139, 92, 246, 0.08)' : 'rgba(139, 92, 246, 0.06)',
      accent: isDarkMode ? '#a78bfa' : '#7c3aed',
      accentMuted: isDarkMode ? 'rgba(167, 139, 250, 0.15)' : 'rgba(124, 58, 237, 0.08)',
      connector: isDarkMode ? 'rgba(139, 92, 246, 0.25)' : 'rgba(139, 92, 246, 0.15)',
      text: isDarkMode ? '#e0d5ff' : '#5b21b6',
      valueBg: isDarkMode ? 'rgba(139, 92, 246, 0.12)' : 'rgba(139, 92, 246, 0.06)',
    },
    teal: {
      barFrom: isDarkMode ? 'rgba(45, 212, 191, 0.30)' : 'rgba(45, 212, 191, 0.15)',
      barTo: isDarkMode ? 'rgba(45, 212, 191, 0.06)' : 'rgba(45, 212, 191, 0.05)',
      accent: isDarkMode ? '#5eead4' : '#0d9488',
      accentMuted: isDarkMode ? 'rgba(94, 234, 212, 0.15)' : 'rgba(13, 148, 136, 0.08)',
      connector: isDarkMode ? 'rgba(45, 212, 191, 0.20)' : 'rgba(45, 212, 191, 0.12)',
      text: isDarkMode ? '#a7f3d0' : '#065f46',
      valueBg: isDarkMode ? 'rgba(45, 212, 191, 0.10)' : 'rgba(45, 212, 191, 0.05)',
    },
  };
  const s = schemes[colorScheme] || schemes.violet;
  const maxVal = Math.max(...stages.map(st => st.value), 1);

  return (
    <div className="relative" data-testid="funnel-shape">
      <div className="space-y-2">
        {stages.map((stage, i) => {
          const widthPct = Math.max((stage.value / maxVal) * 100, 8);
          const hasRate = i < stages.length - 1 && stage.rate !== undefined;
          return (
            <div key={stage.label}>
              {/* Bar row */}
              <div className="flex items-center gap-3">
                <span className={`text-[10px] font-semibold uppercase tracking-wider w-20 text-right shrink-0 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>{stage.label}</span>
                <div className="flex-1 relative">
                  <div
                    className="h-8 rounded-md relative overflow-hidden transition-all duration-700 ease-out"
                    style={{
                      width: `${widthPct}%`,
                      background: `linear-gradient(90deg, ${s.barFrom}, ${s.barTo})`,
                      border: `1px solid ${s.connector}`,
                      minWidth: '60px',
                    }}
                  >
                    {/* Inner glow line */}
                    <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, ${s.accent}33, transparent)` }} />
                    {/* Value inside bar */}
                    <div className="absolute inset-0 flex items-center px-3">
                      <span className="text-sm font-bold" style={{ color: s.accent }}>{stage.value.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Conversion connector */}
              {hasRate && (
                <div className="flex items-center gap-3 my-0.5">
                  <div className="w-20 shrink-0" />
                  <div className="flex items-center gap-2 pl-4">
                    <div className="w-px h-3" style={{ background: s.connector }} />
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded"
                      style={{ background: s.accentMuted, color: s.accent }}
                    >
                      {stage.rate}%
                    </span>
                    <ChevronDown size={10} style={{ color: s.accent }} className="opacity-40" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Date presets
const DATE_PRESETS = [
  { label: 'Last 7 Days', getValue: () => ({ from: subDays(new Date(), 7), to: new Date() }) },
  { label: 'Last 14 Days', getValue: () => ({ from: subDays(new Date(), 14), to: new Date() }) },
  { label: 'Last 30 Days', getValue: () => ({ from: subDays(new Date(), 30), to: new Date() }) },
  { label: 'This Week', getValue: () => ({ from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: new Date() }) },
  { label: 'Last Week', getValue: () => ({ from: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), to: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }) }) },
  { label: 'This Month', getValue: () => ({ from: startOfMonth(new Date()), to: new Date() }) },
  { label: 'Last Month', getValue: () => ({ from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) }) },
  { label: 'Last 90 Days', getValue: () => ({ from: subDays(new Date(), 90), to: new Date() }) },
];

// Date Range Picker component
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const DateRangePicker = ({ dateRange, setDateRange, isDarkMode }) => {
  const [open, setOpen] = useState(false);
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  const handlePreset = (preset) => {
    setDateRange(preset.getValue());
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="date-range-picker-trigger"
          className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${cardBorder} text-sm transition-all ${
            isDarkMode ? 'bg-white/[0.04] hover:bg-white/[0.08] text-gray-300' : 'bg-white hover:bg-gray-50 text-gray-700'
          }`}
        >
          <CalendarDays size={14} className={textSecondary} />
          <span className="font-medium">
            {format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d, yyyy')}
          </span>
          <ChevronDown size={14} className={textSecondary} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end" sideOffset={8}>
        <div className={`flex ${isDarkMode ? 'bg-[#0c0d12] border-white/[0.08]' : 'bg-white border-gray-200'} rounded-xl border overflow-hidden`}>
          {/* Presets */}
          <div className={`w-40 border-r ${isDarkMode ? 'border-white/[0.06]' : 'border-gray-100'} p-2`}>
            <p className={`text-[10px] font-medium uppercase tracking-wider px-2 py-1.5 ${textSecondary}`}>Quick Select</p>
            {DATE_PRESETS.map(p => (
              <button
                key={p.label}
                onClick={() => handlePreset(p)}
                className={`w-full text-left px-2 py-1.5 rounded-lg text-xs transition-colors ${
                  isDarkMode ? 'hover:bg-white/[0.06] text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {/* Calendar */}
          <div className="p-2">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={(range) => {
                if (range?.from && range?.to) {
                  setDateRange(range);
                  setOpen(false);
                } else if (range?.from) {
                  setDateRange({ ...dateRange, from: range.from });
                }
              }}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
              className={isDarkMode ? '[&_.rdp-day]:text-gray-200 [&_.rdp-caption_label]:text-white' : ''}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Stat card component
const StatCard = ({ label, value, icon: Icon, color, isDarkMode, delay = 0 }) => {
  const cardBg = isDarkMode ? 'bg-[#0c0d12]/80' : 'bg-white/80';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const colorMap = {
    violet: { bg: 'bg-violet-400/10', text: 'text-violet-400' },
    green: { bg: 'bg-green-400/10', text: 'text-green-400' },
    teal: { bg: 'bg-teal-400/10', text: 'text-teal-400' },
    orange: { bg: 'bg-orange-400/10', text: 'text-orange-400' },
  };
  const c = colorMap[color] || colorMap.violet;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className={`${cardBg} backdrop-blur-xl rounded-xl border ${cardBorder} px-3 py-3`}
      data-testid={`stat-${label.toLowerCase().replace(/ /g, '-')}`}
    >
      <div className="flex items-center gap-2.5">
        <div className={`w-7 h-7 rounded-lg ${c.bg} flex items-center justify-center shrink-0`}>
          <Icon className={c.text} size={14} />
        </div>
        <div className="min-w-0">
          <p className={`text-lg font-bold ${textPrimary} tabular-nums leading-tight`}>{value}</p>
          <p className={`text-[10px] ${textSecondary} leading-tight`}>{label}</p>
        </div>
      </div>
    </motion.div>
  );
};

// Funnel step
const FunnelStep = ({ label, value, desc, icon: Icon, color, rate, rateLabel, isDarkMode, showArrow }) => {
  const inputBg = isDarkMode ? 'bg-white/[0.05]' : 'bg-gray-50';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const colorMap = {
    violet: { bg: 'bg-violet-400/10', text: 'text-violet-400', bar: 'bg-violet-400' },
    green: { bg: 'bg-green-400/10', text: 'text-green-400', bar: 'bg-green-400' },
    teal: { bg: 'bg-teal-400/10', text: 'text-teal-400', bar: 'bg-teal-400' },
    orange: { bg: 'bg-orange-400/10', text: 'text-orange-400', bar: 'bg-orange-400' },
  };
  const c = colorMap[color] || colorMap.violet;
  return (
    <div className="relative h-full">
      <div className={`h-full flex flex-col justify-between p-3 rounded-xl ${inputBg} border ${cardBorder}`}>
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <div className={`w-6 h-6 rounded-md ${c.bg} flex items-center justify-center`}>
              <Icon className={c.text} size={12} />
            </div>
            <span className={`text-[10px] font-medium ${textSecondary} uppercase tracking-wide`}>{label}</span>
          </div>
          <p className={`text-xl font-bold ${textPrimary}`}>{value}</p>
          <p className={`text-[10px] ${textSecondary}`}>{desc}</p>
        </div>
        {rate !== undefined && (
          <div className="mt-2 flex items-center gap-1.5">
            <div className={`flex-1 h-1 rounded-full ${isDarkMode ? 'bg-white/[0.06]' : 'bg-gray-100'} overflow-hidden`}>
              <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${Math.min(rate, 100)}%` }} />
            </div>
            <span className={`text-[9px] font-semibold ${c.text}`}>{rate}%</span>
          </div>
        )}
        {rate === undefined && <div className="mt-2 h-[14px]" />}
      </div>
      {showArrow && (
        <div className="hidden lg:flex absolute top-1/2 -right-2.5 transform -translate-y-1/2 z-10">
          <ArrowRight size={10} className={textSecondary} />
        </div>
      )}
    </div>
  );
};

// ── Agent Performance comparison — table + chips + grouped bars ──
const AgentPerformanceSection = ({ perf, isDarkMode, api, subAccountId }) => {
  const cardBg = isDarkMode ? 'bg-[#0c0d12]/80' : 'bg-white/80';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inputBg = isDarkMode ? 'bg-white/[0.05]' : 'bg-gray-50';
  const agents = perf?.agents || [];

  const [order, setOrder] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [page, setPage] = useState(0);
  const dragIdRef = useRef(null);
  const editRef = useRef(null);

  useEffect(() => {
    api.get(`/api/sub-accounts/${subAccountId}/dashboard-prefs`)
      .then(r => setOrder(r.data?.agent_order || []))
      .catch(() => {});
  }, [api, subAccountId]);

  useEffect(() => {
    if (!editOpen) return;
    const fn = (e) => { if (editRef.current && !editRef.current.contains(e.target)) setEditOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [editOpen]);

  const ordered = React.useMemo(() => {
    const idx = new Map(order.map((id, i) => [id, i]));
    return [...agents].sort((a, b) => {
      const ai = idx.has(a.agent_id) ? idx.get(a.agent_id) : order.length + agents.indexOf(a);
      const bi = idx.has(b.agent_id) ? idx.get(b.agent_id) : order.length + agents.indexOf(b);
      return ai - bi;
    });
  }, [agents, order]);

  const fullIds = ordered.map(a => a.agent_id);

  const saveOrder = (ids) => {
    setOrder(ids);
    api.put(`/api/sub-accounts/${subAccountId}/dashboard-prefs`, { agent_order: ids }).catch(() => {});
  };

  const moveToTop = (id) => {
    saveOrder([id, ...fullIds.filter(x => x !== id)]);
  };

  const handleDrop = (targetId) => {
    const dragId = dragIdRef.current;
    dragIdRef.current = null;
    if (!dragId || dragId === targetId) return;
    const from = fullIds.indexOf(dragId);
    const to = fullIds.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const ids = [...fullIds];
    ids.splice(from, 1);
    ids.splice(to, 0, dragId);
    saveOrder(ids);
  };

  const PAGE_SIZE = 10;
  const pages = Math.max(1, Math.ceil(ordered.length / PAGE_SIZE));
  const pageAgents = ordered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const rows = expanded ? ordered : ordered.slice(0, 3);

  const cols = [
    { key: 'calls', label: 'Calls' },
    { key: 'answered_pct', label: 'Answered', fmt: v => `${v}%` },
    { key: 'minutes', label: 'Minutes' },
    { key: 'chats', label: 'Chats' },
    { key: 'booked', label: 'Booked' },
    { key: 'rescheduled', label: 'Resched' },
    { key: 'callbacks', label: 'Callbacks' },
    { key: 'textbacks', label: 'Textbacks' },
    { key: 'spend', label: 'Spend', fmt: v => `$${(v ?? 0).toFixed(2)}`, noBest: true },
  ];
  const best = {};
  cols.forEach(c => {
    if (c.noBest) return;
    const vals = rows.map(r => r[c.key]);
    best[c.key] = vals.length && Math.max(...vals) > 0 ? Math.max(...vals) : null;
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
      className={`${cardBg} backdrop-blur-xl rounded-xl border ${cardBorder} p-5 mb-4`}
      data-testid="agent-performance-section"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400/20 to-sky-400/20 flex items-center justify-center">
            <Bot className="text-teal-400" size={16} />
          </div>
          <div>
            <h3 className={`text-base font-semibold ${textPrimary}`}>Agent Performance</h3>
            <p className={`text-xs ${textSecondary}`}>Your AI agents at a glance</p>
          </div>
        </div>

        {/* Edit order */}
        <div className="relative" ref={editRef}>
          <button
            onClick={() => setEditOpen(o => !o)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold transition-colors ${
              editOpen
                ? 'bg-teal-400/15 text-teal-400 border-teal-400/40'
                : `${textSecondary} ${cardBorder} hover:border-teal-400/40`
            }`}
            data-testid="perf-edit-order-btn"
          >
            <SlidersHorizontal size={12} /> Edit order
          </button>
          <AnimatePresence>
            {editOpen && (
              <motion.div
                initial={{ opacity: 0, y: 4, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.98 }}
                transition={{ duration: 0.15 }}
                className={`absolute right-0 mt-2 w-80 rounded-xl border shadow-2xl z-50 ${
                  isDarkMode ? 'bg-[#0c0d12] border-white/[0.1]' : 'bg-white border-gray-200'
                }`}
                onMouseDown={(e) => e.stopPropagation()}
                data-testid="perf-order-panel"
              >
                <p className={`px-3.5 pt-3 pb-2 text-[10px] uppercase tracking-[0.16em] font-bold ${textSecondary}`}>
                  Drag to reorder — top 3 show by default
                </p>
                <div className="max-h-80 overflow-y-auto px-2 pb-2 space-y-1">
                  {pageAgents.map((a) => {
                    const pos = fullIds.indexOf(a.agent_id);
                    return (
                      <div
                        key={a.agent_id}
                        draggable
                        onDragStart={() => { dragIdRef.current = a.agent_id; }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => handleDrop(a.agent_id)}
                        className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg border cursor-grab active:cursor-grabbing select-none ${
                          pos < 3 ? 'border-teal-400/30 bg-teal-400/[0.06]' : `${cardBorder} ${inputBg}`
                        }`}
                        data-testid={`perf-order-item-${a.agent_id}`}
                      >
                        <GripVertical size={13} className={`${textSecondary} shrink-0`} />
                        <span className={`w-5 text-[10px] font-bold tabular-nums shrink-0 ${pos < 3 ? 'text-teal-400' : textSecondary}`}>{pos + 1}</span>
                        <div className="min-w-0 flex-1">
                          <p className={`text-xs font-semibold truncate ${textPrimary}`}>{a.name}</p>
                          {a.campaign_label && <p className="text-[10px] text-teal-400 truncate">{a.campaign_label}</p>}
                        </div>
                        {pos >= 3 && (
                          <button
                            onClick={() => moveToTop(a.agent_id)}
                            title="Move to top"
                            className={`p-1 rounded shrink-0 ${textSecondary} hover:text-teal-400`}
                            data-testid={`perf-order-top-${a.agent_id}`}
                          >
                            <ArrowUpToLine size={12} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {pages > 1 && (
                  <div className={`flex items-center justify-center gap-3 border-t ${cardBorder} py-2`}>
                    <button
                      disabled={page === 0}
                      onClick={() => setPage(p => p - 1)}
                      className={`p-1 rounded ${textSecondary} hover:text-teal-400 disabled:opacity-30`}
                      data-testid="perf-order-prev"
                    ><ChevronLeft size={14} /></button>
                    <span className={`text-[11px] tabular-nums ${textSecondary}`} data-testid="perf-order-page">{page + 1}/{pages}</span>
                    <button
                      disabled={page === pages - 1}
                      onClick={() => setPage(p => p + 1)}
                      className={`p-1 rounded ${textSecondary} hover:text-teal-400 disabled:opacity-30`}
                      data-testid="perf-order-next"
                    ><ChevronRight size={14} /></button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="agent-performance-table">
          <thead>
            <tr className={`text-[10px] uppercase tracking-wider ${textSecondary} border-b ${cardBorder}`}>
              <th className="text-left font-semibold py-2.5 pr-3">Agent / Campaign</th>
              {cols.map(c => <th key={c.key} className="text-right font-semibold py-2.5 px-3">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={10} className={`py-6 text-center text-xs ${textSecondary}`}>No agents to compare</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.agent_id} className={`border-b ${cardBorder} last:border-0`} data-testid={`perf-row-${r.agent_id}`}>
                <td className="py-2.5 pr-3">
                  <div className={`font-semibold ${textPrimary} text-[13px]`}>{r.name}</div>
                  {r.campaign_label && <div className="text-[10px] font-medium text-teal-400">{r.campaign_label}</div>}
                </td>
                {cols.map(c => {
                  const v = r[c.key];
                  const isBest = best[c.key] !== null && v === best[c.key];
                  return (
                    <td key={c.key} className={`text-right py-2.5 px-3 tabular-nums ${isBest ? 'text-teal-400 font-bold' : textPrimary}`}>
                      {c.fmt ? c.fmt(v) : v}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {ordered.length > 3 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className={`mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-semibold transition-colors ${textSecondary} ${cardBorder} hover:border-teal-400/40 hover:text-teal-400`}
          data-testid="perf-show-all-btn"
        >
          {expanded ? 'Show top 3' : `Show all (${ordered.length})`}
          <ChevronDown size={13} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      )}
    </motion.div>
  );
};

// ── Branded PDF report modal — period, compare, agent filter, history ──
const isoWeekRange = (weekStr) => {
  const [y, w] = weekStr.split('-W').map(Number);
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + (w - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const f = (d) => d.toISOString().slice(0, 10);
  return [f(monday), f(sunday)];
};

const ReportModal = ({ api, subAccountId, agents, isDarkMode, onClose }) => {
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inputBg = isDarkMode ? 'bg-white/[0.05]' : 'bg-gray-50';
  const today = new Date();
  const [periodType, setPeriodType] = useState('month');
  const [dayVal, setDayVal] = useState(format(today, 'yyyy-MM-dd'));
  const [weekVal, setWeekVal] = useState('');
  const [monthVal, setMonthVal] = useState(format(today, 'yyyy-MM'));
  const [compare, setCompare] = useState(true);
  const [selIds, setSelIds] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => { fetchHistory(); }, []);
  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/reports`);
      setHistory(res.data || []);
    } catch { setHistory([]); }
    finally { setLoadingHistory(false); }
  };

  const downloadReport = async (reportId, label) => {
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/reports/${reportId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AI_Report_${(label || reportId).replace(/[^\w-]+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed — try regenerating the report');
    }
  };

  const range = () => {
    if (periodType === 'day') return [dayVal, dayVal];
    if (periodType === 'week') return weekVal ? isoWeekRange(weekVal) : [null, null];
    if (periodType === 'month' && monthVal) {
      const [y, m] = monthVal.split('-').map(Number);
      const last = new Date(y, m, 0).getDate();
      return [`${monthVal}-01`, `${monthVal}-${String(last).padStart(2, '0')}`];
    }
    return [null, null];
  };

  const generate = async () => {
    const [start, end] = range();
    if (!start || !end) { toast.error('Pick a period first'); return; }
    setGenerating(true);
    try {
      const res = await api.post(`/api/sub-accounts/${subAccountId}/reports/generate`, {
        start_date: start, end_date: end, compare,
        agent_ids: selIds.length ? selIds : null,
        tz: BROWSER_TZ,
      });
      toast.success('Report generated');
      await downloadReport(res.data.report_id, res.data.period_label);
      fetchHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Report generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const deleteReport = async (reportId) => {
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/reports/${reportId}`);
      setHistory(h => h.filter(r => r.report_id !== reportId));
    } catch { toast.error('Delete failed'); }
  };

  const toggleAgent = (id) => setSelIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const inputCls = `w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none focus:border-teal-400/50`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="report-modal">
      <div
        className={`w-full max-w-xl max-h-[88vh] flex flex-col rounded-2xl ${isDarkMode ? 'bg-[#101117]' : 'bg-white'} border ${cardBorder} shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-5 py-4 border-b ${cardBorder} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-teal-400" />
            <h3 className={`text-base font-semibold ${textPrimary}`}>Performance Report</h3>
          </div>
          <button onClick={onClose} className={`p-1 rounded ${textSecondary} hover:${textPrimary}`}><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className={`block text-xs ${textSecondary} mb-1.5 font-semibold uppercase tracking-wider`}>Period</label>
            <div className={`flex rounded-xl ${inputBg} border ${cardBorder} p-1 mb-2`}>
              {[['day', 'Day'], ['week', 'Week'], ['month', 'Month']].map(([id, label]) => (
                <button key={id} onClick={() => setPeriodType(id)}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    periodType === id ? 'bg-teal-400 text-[#08090d]' : `${textSecondary}`
                  }`}
                  data-testid={`report-period-${id}`}
                >{label}</button>
              ))}
            </div>
            {periodType === 'day' && <input type="date" value={dayVal} onChange={e => setDayVal(e.target.value)} className={inputCls} data-testid="report-day-input" />}
            {periodType === 'week' && <input type="week" value={weekVal} onChange={e => setWeekVal(e.target.value)} className={inputCls} data-testid="report-week-input" />}
            {periodType === 'month' && <input type="month" value={monthVal} onChange={e => setMonthVal(e.target.value)} className={inputCls} data-testid="report-month-input" />}
          </div>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <div className={`text-sm font-medium ${textPrimary}`}>Compare with previous period</div>
              <div className={`text-[11px] ${textSecondary}`}>Adds ▲/▼ change indicators next to every metric</div>
            </div>
            <button
              onClick={() => setCompare(!compare)}
              className={`w-11 h-6 rounded-full transition-colors ${compare ? 'bg-teal-400' : 'bg-white/20'}`}
              data-testid="report-compare-toggle"
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${compare ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </label>

          <div>
            <label className={`block text-xs ${textSecondary} mb-1.5 font-semibold uppercase tracking-wider`}>Agents to include</label>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setSelIds([])}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${selIds.length === 0 ? 'bg-teal-400 text-[#08090d] border-teal-400' : `${textSecondary} ${cardBorder}`}`}
                data-testid="report-agents-all"
              >All agents</button>
              {agents.map(a => (
                <button key={a.agent_id} onClick={() => toggleAgent(a.agent_id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${selIds.includes(a.agent_id) ? 'bg-teal-400/15 text-teal-400 border-teal-400/40' : `${textSecondary} ${cardBorder}`}`}
                >{a.name}{a.campaign_label ? ` · ${a.campaign_label}` : ''}</button>
              ))}
            </div>
          </div>

          <button
            onClick={generate}
            disabled={generating}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] text-sm font-semibold hover:shadow-lg hover:shadow-teal-400/25 transition-all disabled:opacity-60"
            data-testid="report-generate-btn"
          >
            {generating ? <><Loader2 size={15} className="animate-spin" /> Building your report…</> : <><Download size={15} /> Generate & Download PDF</>}
          </button>

          <div className={`pt-3 border-t ${cardBorder}`}>
            <label className={`block text-xs ${textSecondary} mb-2 font-semibold uppercase tracking-wider`}>Report history</label>
            {loadingHistory ? (
              <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-teal-400" /></div>
            ) : history.length === 0 ? (
              <p className={`text-xs ${textSecondary} py-2`}>No reports generated yet.</p>
            ) : (
              <div className="space-y-1.5" data-testid="report-history-list">
                {history.map(r => (
                  <div key={r.report_id} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${inputBg} border ${cardBorder}`} data-testid={`report-row-${r.report_id}`}>
                    <FileText size={14} className="text-teal-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-medium ${textPrimary} truncate`}>{r.period_label}</div>
                      <div className={`text-[10px] ${textSecondary}`}>
                        {format(new Date(r.created_at), 'MMM d, h:mm a')}{r.created_by_name ? ` · ${r.created_by_name}` : ''} · {(r.file_size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                    <button onClick={() => downloadReport(r.report_id, r.period_label)} className={`p-1.5 rounded-lg ${textSecondary} hover:text-teal-400`} title="Download" data-testid={`report-download-${r.report_id}`}>
                      <Download size={13} />
                    </button>
                    <button onClick={() => deleteReport(r.report_id)} className={`p-1.5 rounded-lg ${textSecondary} hover:text-red-400`} title="Delete">
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SubAccountDashboardContent = () => {
  const { workspaceId, subAccountId } = useParams();
  const { api } = useAuth();
  const { subAccount } = useSubAccount();
  const { isDarkMode } = useTheme();
  const [stats, setStats] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [tsTotals, setTsTotals] = useState(null);
  const [dateRange, setDateRange] = useState({ from: subDays(new Date(), 30), to: new Date() });
  const [ghlStatus, setGhlStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetting, setResetting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(null); // 'csv' | 'pdf' | null
  const [recentCalls, setRecentCalls] = useState([]);
  const [upcomingCallbacks, setUpcomingCallbacks] = useState([]);
  const [transferStats, setTransferStats] = useState(null);
  const [perf, setPerf] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    fetchGhlStatus();
  }, [subAccountId]);

  useEffect(() => {
    fetchDashboardData();
    fetchTimeseries();
    fetchRecentCalls();
    fetchUpcomingCallbacks();
    fetchTransferStats();
    fetchPerformance();
  }, [subAccountId, dateRange]);

  const fetchPerformance = async () => {
    try {
      const startStr = format(dateRange.from, 'yyyy-MM-dd');
      const endStr = format(dateRange.to, 'yyyy-MM-dd');
      const res = await api.get(`/api/sub-accounts/${subAccountId}/agent-performance?start_date=${startStr}&end_date=${endStr}&tz=${encodeURIComponent(BROWSER_TZ)}`).catch(() => ({ data: null }));
      setPerf(res.data);
    } catch { setPerf(null); }
  };

  const fetchTransferStats = async () => {
    try {
      const startStr = format(dateRange.from, 'yyyy-MM-dd');
      const endStr = format(dateRange.to, 'yyyy-MM-dd');
      const res = await api.get(`/api/sub-accounts/${subAccountId}/transfer-stats?start_date=${startStr}&end_date=${endStr}&tz=${encodeURIComponent(BROWSER_TZ)}`).catch(() => ({ data: null }));
      setTransferStats(res.data);
    } catch { setTransferStats(null); }
  };

  const fetchDashboardData = async () => {
    try {
      const startStr = format(dateRange.from, 'yyyy-MM-dd');
      const endStr = format(dateRange.to, 'yyyy-MM-dd');
      const statsRes = await api.get(`/api/sub-accounts/${subAccountId}/stats?start_date=${startStr}&end_date=${endStr}`).catch(() => ({ data: null }));
      setStats(statsRes.data || {
        total_calls: 0, outbound_calls: 0, inbound_calls: 0, answered_calls: 0,
        appointments_booked: 0, total_minutes: 0, voice_agents: 0, chat_agents: 0,
        avg_call_duration: 0, success_rate: 0, chat_tagged: 0, chat_responded: 0,
        chat_booked: 0, voice_booked: 0, agent_funnels: []
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeseries = async () => {
    try {
      const startStr = format(dateRange.from, 'yyyy-MM-dd');
      const endStr = format(dateRange.to, 'yyyy-MM-dd');
      const res = await api.get(`/api/sub-accounts/${subAccountId}/stats/timeseries?start_date=${startStr}&end_date=${endStr}&tz=${encodeURIComponent(BROWSER_TZ)}`).catch(() => ({ data: { data: [], totals: null } }));
      const payload = res.data || {};
      // Support both old array format and new {data, totals} format
      if (Array.isArray(payload)) {
        setTimeseries(payload);
        setTsTotals(null);
      } else {
        setTimeseries(payload.data || []);
        setTsTotals(payload.totals || null);
      }
    } catch (error) {
      console.error('Failed to fetch timeseries:', error);
    }
  };

  const fetchGhlStatus = async () => {
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/ghl/status`).catch(() => ({ data: null }));
      setGhlStatus(res.data);
    } catch { setGhlStatus(null); }
  };

  const fetchRecentCalls = async () => {
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/calls?limit=5`).catch(() => ({ data: [] }));
      // Endpoint returns either an array or {calls:[...]} — support both shapes.
      const list = Array.isArray(res.data) ? res.data : (res.data?.calls || []);
      setRecentCalls(list.slice(0, 5));
    } catch { setRecentCalls([]); }
  };

  const fetchUpcomingCallbacks = async () => {
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/scheduled-calls?status=pending`).catch(() => ({ data: [] }));
      const list = Array.isArray(res.data) ? res.data : [];
      // Sort by soonest first; future-only.
      const now = new Date();
      const upcoming = list
        .filter(cb => cb.scheduled_at && new Date(cb.scheduled_at) >= now)
        .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
      setUpcomingCallbacks(upcoming.slice(0, 5));
    } catch { setUpcomingCallbacks([]); }
  };

  const fetchAiSummary = useCallback(async () => {
    setAiLoading(true);
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/stats/ai-summary`);
      setAiSummary(res.data);
    } catch (error) {
      setAiSummary({ summary: 'Unable to generate AI summary.', status: 'error' });
    } finally {
      setAiLoading(false);
    }
  }, [subAccountId, api]);

  // Close export dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleResetData = async () => {
    if (resetConfirm !== 'RESET') return;
    setResetting(true);
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/reset-data`);
      setResetOpen(false);
      setResetConfirm('');
      // Refresh all data
      setLoading(true);
      await Promise.all([fetchDashboardData(), fetchTimeseries()]);
    } catch (error) {
      console.error('Reset failed:', error);
    } finally {
      setResetting(false);
    }
  };

  const handleExport = async (type) => {
    setExporting(type);
    setExportOpen(false);
    try {
      const startStr = format(dateRange.from, 'yyyy-MM-dd');
      const endStr = format(dateRange.to, 'yyyy-MM-dd');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/sub-accounts/${subAccountId}/export/${type}?start_date=${startStr}&end_date=${endStr}`;
      const res = await api.get(`/api/sub-accounts/${subAccountId}/export/${type}?start_date=${startStr}&end_date=${endStr}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: type === 'csv' ? 'text/csv' : 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `report_${format(new Date(), 'yyyyMMdd')}.${type}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error(`Export ${type} failed:`, error);
    } finally {
      setExporting(null);
    }
  };

  const cardBg = isDarkMode ? 'bg-[#0c0d12]/80' : 'bg-white/80';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const bgMain = isDarkMode ? 'bg-[#08090d]' : 'bg-gray-50';
  const inputBg = isDarkMode ? 'bg-white/[0.05]' : 'bg-gray-50';

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${bgMain}`}>
        <div className="w-14 h-14 border-4 border-teal-400/20 border-t-teal-400 rounded-full animate-spin" />
      </div>
    );
  }

  const topAgent = stats?.top_agent || null;
  const fmtDuration = (s) => { if (!s) return '0:00'; return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`; };

  // ALL values derived from date-range filtered timeseries totals
  const vCalls = tsTotals?.voice_conversations ?? 0;
  const vAnswered = tsTotals?.voice_answered ?? 0;
  const vBooked = tsTotals?.voice_booked ?? 0;
  const vMissed = vCalls - vAnswered;
  const vInbound = tsTotals?.voice_inbound ?? 0;
  const vOutbound = tsTotals?.voice_outbound ?? 0;
  const vMinutes = tsTotals?.voice_minutes ?? 0;
  const vAvgDuration = tsTotals?.voice_avg_duration ?? 0;
  const vAnswerRate = vCalls > 0 ? Math.round(vAnswered / vCalls * 1000) / 10 : 0;
  const vBookRate = vAnswered > 0 ? Math.round(vBooked / vAnswered * 1000) / 10 : 0;

  // Chat: unique contacts across full date range
  const cTagged = tsTotals?.chat_tagged ?? 0;
  const cConvos = tsTotals?.chat_conversations ?? 0;
  const cBooked = tsTotals?.chat_booked ?? 0;
  const cConvoRate = cTagged > 0 ? Math.round(cConvos / cTagged * 1000) / 10 : 0;
  const cBookRate = cTagged > 0 ? Math.round(cBooked / cTagged * 1000) / 10 : 0;

  // Combined totals
  const totalBooked = tsTotals?.total_booked ?? 0;
  const totalRescheduled = tsTotals?.total_rescheduled ?? perf?.totals?.rescheduled ?? 0;
  const voiceChartLines = [
    { key: 'voice_conversations', label: 'Calls', color: '#a78bfa' },
    { key: 'voice_answered', label: 'Answered', color: '#2dd4bf' },
    { key: 'voice_booked', label: 'Booked', color: '#fb923c' },
  ];

  return (
    <div className={`min-h-screen ${bgMain} p-5 lg:p-7`} data-testid="sub-account-dashboard">
      {isDarkMode && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-teal-400/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-violet-400/5 rounded-full blur-[100px]" />
        </div>
      )}

      <div className="relative z-10 max-w-[1600px] mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-violet-500 flex items-center justify-center">
                  <Headphones className="text-white" size={20} />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-[#08090d] animate-pulse" />
              </div>
              <div>
                <h1 className={`text-xl lg:text-2xl font-display font-semibold ${textPrimary}`}>{subAccount?.name}</h1>
                <p className={`text-sm ${textSecondary}`}>Performance Analytics</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} isDarkMode={isDarkMode} />
              <button
                onClick={() => setReportOpen(true)}
                data-testid="open-report-modal-btn"
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-all ${
                  isDarkMode
                    ? 'bg-teal-400/[0.08] hover:bg-teal-400/[0.15] border-teal-400/25 text-teal-300'
                    : 'bg-teal-50 hover:bg-teal-100 border-teal-200 text-teal-700'
                }`}
              >
                <FileText size={14} />
                Report
              </button>
              {/* Export Dropdown */}
              <div className="relative" ref={exportRef}>
                <button
                  onClick={() => setExportOpen(!exportOpen)}
                  disabled={!!exporting}
                  data-testid="export-btn"
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-all ${
                    isDarkMode
                      ? 'bg-white/[0.04] hover:bg-white/[0.08] border-white/[0.08] text-gray-300'
                      : 'bg-white hover:bg-gray-50 border-gray-200 text-gray-700'
                  }`}
                >
                  {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  Export
                  <ChevronDown size={12} />
                </button>
                <AnimatePresence>
                  {exportOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 4, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 4, scale: 0.97 }}
                      transition={{ duration: 0.15 }}
                      className={`absolute right-0 mt-2 w-44 rounded-xl border shadow-xl z-50 overflow-hidden ${
                        isDarkMode ? 'bg-[#0c0d12] border-white/[0.08]' : 'bg-white border-gray-200'
                      }`}
                    >
                      <button
                        onClick={() => handleExport('csv')}
                        data-testid="export-csv-btn"
                        className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors ${
                          isDarkMode ? 'hover:bg-white/[0.06] text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <FileSpreadsheet size={15} className="text-green-400" />
                        Export as CSV
                      </button>
                      <button
                        onClick={() => handleExport('pdf')}
                        data-testid="export-pdf-btn"
                        className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors ${
                          isDarkMode ? 'hover:bg-white/[0.06] text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <FileText size={15} className="text-red-400" />
                        Export as PDF
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {/* Reset Data Button */}
              <button
                onClick={() => setResetOpen(true)}
                data-testid="reset-data-btn"
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-all ${
                  isDarkMode
                    ? 'bg-red-500/[0.08] hover:bg-red-500/[0.15] border-red-500/20 text-red-400'
                    : 'bg-red-50 hover:bg-red-100 border-red-200 text-red-600'
                }`}
              >
                <RotateCcw size={14} />
                Reset
              </button>
              <Link
                to={`/workspace/${workspaceId}/sub-account/${subAccountId}/agents`}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] text-sm font-medium hover:shadow-lg hover:shadow-teal-400/25 transition-all"
                data-testid="create-agent-btn"
              >
                <Sparkles size={16} /> Agents
              </Link>
            </div>
          </div>
        </motion.div>

        {/* LeadConnector Connection Warning */}
        {ghlStatus && ghlStatus.needs_reconnect && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className={`mb-4 flex items-center gap-3 px-4 py-3 rounded-xl border ${
              isDarkMode ? 'bg-red-500/[0.08] border-red-500/20' : 'bg-red-50 border-red-200'
            }`}
            data-testid="ghl-reauth-warning"
          >
            <AlertTriangle className="text-red-400 shrink-0" size={18} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                CRM connection expired — chat agents are paused
              </p>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-red-400/70' : 'text-red-500/80'}`}>
                Your LeadConnector session needs re-authorization. Click reconnect in Settings to resume.
              </p>
            </div>
            <Link
              to={`/workspace/${workspaceId}/sub-account/${subAccountId}/settings?tab=billing`}
              className="shrink-0 px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600 transition-colors"
              data-testid="ghl-reconnect-link"
            >
              Reconnect
            </Link>
          </motion.div>
        )}

        {/* Top Stats — evenly-sized cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-2.5 mb-4">
          <StatCard label="Inbound Calls" value={vInbound} icon={PhoneIncoming} color="violet" isDarkMode={isDarkMode} delay={0.03} />
          <StatCard label="Outbound Calls" value={vOutbound} icon={PhoneOutgoing} color="teal" isDarkMode={isDarkMode} delay={0.05} />
          <StatCard label="Answered" value={vAnswered} icon={CheckCircle} color="green" isDarkMode={isDarkMode} delay={0.06} />
          <StatCard label="Chat Conversations" value={cTagged} icon={MessageSquare} color="teal" isDarkMode={isDarkMode} delay={0.09} />
          <StatCard label="Total Booked" value={totalBooked} icon={CalendarIcon} color="orange" isDarkMode={isDarkMode} delay={0.12} />
          <StatCard label="Rescheduled" value={totalRescheduled} icon={RotateCcw} color="teal" isDarkMode={isDarkMode} delay={0.13} />
          <StatCard label="Callbacks" value={perf?.totals?.callbacks ?? 0} icon={PhoneCall} color="teal" isDarkMode={isDarkMode} delay={0.14} />
          <StatCard label="Textbacks" value={perf?.totals?.textbacks ?? 0} icon={Send} color="violet" isDarkMode={isDarkMode} delay={0.16} />
          <StatCard label="Minutes Used" value={vMinutes} icon={BarChart3} color="teal" isDarkMode={isDarkMode} delay={0.18} />
        </div>

        {/* Spend & ROI row */}
        {perf?.totals && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-4" data-testid="spend-roi-row">
            <StatCard label="Voice Spend" value={`$${(perf.totals.voice_spend ?? 0).toFixed(2)}`} icon={DollarSign} color="violet" isDarkMode={isDarkMode} delay={0.22} />
            <StatCard label="Text Spend" value={`$${(perf.totals.text_spend ?? 0).toFixed(2)}`} icon={DollarSign} color="teal" isDarkMode={isDarkMode} delay={0.24} />
            <StatCard label="Cost / Booked · Voice" value={perf.totals.cost_per_booked_voice != null ? `$${perf.totals.cost_per_booked_voice.toFixed(2)}` : '—'} icon={Target} color="orange" isDarkMode={isDarkMode} delay={0.26} />
            <StatCard label="Cost / Booked · Text" value={perf.totals.cost_per_booked_text != null ? `$${perf.totals.cost_per_booked_text.toFixed(2)}` : '—'} icon={Target} color="orange" isDarkMode={isDarkMode} delay={0.28} />
            <StatCard label="Follow-Up Conversion" value={`${perf.totals.followup_conversion_pct ?? 0}%`} icon={TrendingUp} color="green" isDarkMode={isDarkMode} delay={0.3} />
            <StatCard label="Avg Duration" value={fmtDuration(vAvgDuration)} icon={Timer} color="violet" isDarkMode={isDarkMode} delay={0.32} />
          </div>
        )}

        {/* Agent Performance — per-agent campaign comparison */}
        {perf && perf.agents && perf.agents.length > 0 && (
          <AgentPerformanceSection
            perf={perf}
            isDarkMode={isDarkMode}
            api={api}
            subAccountId={subAccountId}
          />
        )}

        {/* Voice Trend line chart — multi-line view of Calls / Answered /
            Booked over time. Funnel cards live in Performance Tracking
            below; this card is purely the trend visualization. */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`${cardBg} backdrop-blur-xl rounded-xl border ${cardBorder} p-4 mb-3`}
          data-testid="voice-trend-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-400/20 to-violet-600/20 flex items-center justify-center">
                <Mic className="text-violet-400" size={16} />
              </div>
              <div>
                <h3 className={`text-base font-semibold ${textPrimary}`}>Voice Trend</h3>
                <p className={`text-xs ${textSecondary}`}>Calls, answered & booked over time</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {voiceChartLines.map(l => (
                <div key={l.key} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} />
                  <span className={`text-[10px] ${textSecondary}`}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>
          <LineChart data={timeseries} lines={voiceChartLines} height={220} isDarkMode={isDarkMode} />
        </motion.div>

        {/* Conversion Trend + 2-col Performance summary — single unified
            view that replaces the old Voice/Chat Performance sections.
            Same metrics, rolled up so a client can read the entire month
            at a glance. */}
        {(() => {
          // Compute trend % — last 7 days conversions vs prior 7 days
          const totals = timeseries.map(d => (d.voice_conversations || 0) + (d.chat_conversations || 0));
          const half = Math.ceil(totals.length / 2);
          const recent = totals.slice(-half).reduce((a, b) => a + b, 0);
          const prior = totals.slice(0, totals.length - half).reduce((a, b) => a + b, 0);
          const trendPct = prior > 0 ? Math.round(((recent - prior) / prior) * 100) : (recent > 0 ? 100 : 0);
          const trendUp = trendPct >= 0;

          // Voice + Chat side-by-side — uses the SAME values as the sections above/below
          const vMaxV = Math.max(vCalls, vAnswered, vBooked, 1);
          const cMaxV = Math.max(cTagged, cConvos, cBooked, 1);
          const voiceRows = [
            { lbl: "Calls", val: vCalls, w: `${(vCalls / vMaxV) * 100}%`, color: "from-violet-400/45 to-violet-400/10" },
            { lbl: "Answered", val: vAnswered, w: `${(vAnswered / vMaxV) * 100}%`, color: "from-emerald-400/55 to-emerald-400/15" },
            { lbl: "Booked", val: vBooked, w: `${(vBooked / vMaxV) * 100}%`, color: "from-orange-400/65 to-orange-400/20" },
          ];
          const chatRows = [
            { lbl: "Chats", val: cTagged, w: `${(cTagged / cMaxV) * 100}%`, color: "from-teal-400/45 to-teal-400/10" },
            { lbl: "Replied", val: cConvos, w: `${(cConvos / cMaxV) * 100}%`, color: "from-blue-400/55 to-blue-400/15" },
            { lbl: "Booked", val: cBooked, w: `${(cBooked / cMaxV) * 100}%`, color: "from-orange-400/65 to-orange-400/20" },
          ];
          return (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.13 }}
              className={`${cardBg} backdrop-blur-xl rounded-xl border ${cardBorder} p-4 mb-3`}
              data-testid="conversion-trend-card"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400/20 to-teal-600/20 flex items-center justify-center">
                    <BarChart3 className="text-teal-400" size={16} />
                  </div>
                  <div>
                    <h3 className={`text-base font-semibold ${textPrimary}`}>Performance Tracking</h3>
                    <p className={`text-xs ${textSecondary}`}>Voice + Chat conversions side-by-side</p>
                  </div>
                </div>
                <span className={`text-xs flex items-center gap-1 font-medium ${trendUp ? "text-teal-400" : "text-red-400"}`}>
                  <TrendingUp size={13} className={trendUp ? "" : "rotate-180"} />
                  {trendUp ? "+" : ""}{trendPct}%
                </span>
              </div>

              {/* 2-column performance summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Voice column */}
                <div className={`rounded-lg ${inputBg} border ${cardBorder} p-3`}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Headphones size={12} className="text-violet-300" />
                    <span className="text-[10px] uppercase tracking-[0.16em] text-violet-300 font-bold">Voice Performance</span>
                  </div>
                  <div className="space-y-2.5">
                    {voiceRows.map((f, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-[11px] ${textSecondary}`}>{f.lbl}</span>
                          <span className={`text-sm ${textPrimary} font-bold tabular-nums`}>{f.val}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                          <div className={`h-full bg-gradient-to-r ${f.color} rounded-full transition-all duration-500`} style={{ width: f.w }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Chat column */}
                <div className={`rounded-lg ${inputBg} border ${cardBorder} p-3`}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <MessageSquare size={12} className="text-teal-300" />
                    <span className="text-[10px] uppercase tracking-[0.16em] text-teal-300 font-bold">Chat Performance</span>
                  </div>
                  <div className="space-y-2.5">
                    {chatRows.map((f, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-[11px] ${textSecondary}`}>{f.lbl}</span>
                          <span className={`text-sm ${textPrimary} font-bold tabular-nums`}>{f.val}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                          <div className={`h-full bg-gradient-to-r ${f.color} rounded-full transition-all duration-500`} style={{ width: f.w }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })()}

        {/* Recent Calls + Upcoming Callbacks — 2-column row that replaces
            the old Chat Performance + agent funnels block. Both sides
            populate from real endpoints (recentCalls + upcomingCallbacks
            state below). */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          {/* ── Recent Calls ── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
            className={`${cardBg} backdrop-blur-xl rounded-xl border ${cardBorder} p-4`}
            data-testid="recent-calls-card"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-400/20 to-violet-600/20 flex items-center justify-center">
                  <Phone className="text-violet-400" size={16} />
                </div>
                <div>
                  <h3 className={`text-base font-semibold ${textPrimary}`}>Recent Calls</h3>
                  <p className={`text-xs ${textSecondary}`}>Latest call activity</p>
                </div>
              </div>
              <Link
                to={`/sub-account/${subAccountId}/calls`}
                className={`text-[11px] ${textSecondary} hover:${textPrimary} flex items-center gap-1`}
                data-testid="recent-calls-view-all"
              >
                View all <ArrowRight size={11} />
              </Link>
            </div>

            {recentCalls.length === 0 ? (
              <div className={`text-center py-8 ${textSecondary}`}>
                <Phone size={20} className="mx-auto mb-2 opacity-50" />
                <p className="text-xs">No calls yet</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {recentCalls.slice(0, 5).map(call => {
                  const dirIn = (call.direction || "").toLowerCase() === "inbound" || (call.direction || "").toLowerCase() === "in";
                  const outcome = (call.outcome || call.status || "").toLowerCase();
                  const outcomeBadge = outcome.includes("book") ? { c: "text-orange-400 bg-orange-400/10 border-orange-400/20", l: "Booked" }
                    : outcome.includes("voicemail") ? { c: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20", l: "Voicemail" }
                    : outcome.includes("no_answer") || outcome.includes("missed") ? { c: "text-red-400 bg-red-400/10 border-red-400/20", l: "No Answer" }
                    : outcome.includes("complete") ? { c: "text-teal-400 bg-teal-400/10 border-teal-400/20", l: "Completed" }
                    : { c: `${textSecondary} bg-white/[0.04] border-white/[0.06]`, l: outcome || "—" };
                  const dur = call.duration ? `${Math.floor(call.duration / 60)}:${String(call.duration % 60).padStart(2, "0")}` : "—";
                  return (
                    <Link
                      to={`/sub-account/${subAccountId}/calls/${call.call_id}`}
                      key={call.call_id}
                      className={`flex items-center justify-between p-2.5 rounded-lg ${inputBg} border ${cardBorder} hover:border-violet-400/30 transition-colors`}
                      data-testid={`recent-call-${call.call_id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${dirIn ? "bg-violet-400/10 border border-violet-400/20" : "bg-teal-400/10 border border-teal-400/20"}`}>
                          {dirIn ? <Phone size={12} className="text-violet-400" /> : <ArrowRight size={12} className="text-teal-400" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-xs font-medium ${textPrimary} truncate`}>
                            {call.contact_name || call.phone_number || "Unknown"}
                          </p>
                          <p className={`text-[10px] ${textSecondary} truncate`}>
                            {call.agent_name || "—"} · {dirIn ? "Inbound" : "Outbound"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-[9px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded ${outcomeBadge.c}`}>{outcomeBadge.l}</span>
                        <span className={`text-[10px] ${textSecondary} font-mono tabular-nums w-10 text-right`}>{dur}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* ── Upcoming Callbacks ── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            className={`${cardBg} backdrop-blur-xl rounded-xl border ${cardBorder} p-4`}
            data-testid="upcoming-callbacks-card"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-400/20 to-orange-600/20 flex items-center justify-center">
                  <CalendarIcon className="text-orange-400" size={16} />
                </div>
                <div>
                  <h3 className={`text-base font-semibold ${textPrimary}`}>Upcoming Callbacks</h3>
                  <p className={`text-xs ${textSecondary}`}>Scheduled to auto-execute</p>
                </div>
              </div>
              <Link
                to={`/sub-account/${subAccountId}/callbacks`}
                className={`text-[11px] ${textSecondary} hover:${textPrimary} flex items-center gap-1`}
                data-testid="upcoming-callbacks-view-all"
              >
                View all <ArrowRight size={11} />
              </Link>
            </div>

            {upcomingCallbacks.length === 0 ? (
              <div className={`text-center py-8 ${textSecondary}`}>
                <CalendarIcon size={20} className="mx-auto mb-2 opacity-50" />
                <p className="text-xs">No callbacks scheduled</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {upcomingCallbacks.slice(0, 5).map(cb => {
                  const when = cb.scheduled_at ? new Date(cb.scheduled_at) : null;
                  const whenStr = when
                    ? when.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + " · " + when.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
                    : "—";
                  return (
                    <div
                      key={cb.call_id}
                      className={`flex items-center justify-between p-2.5 rounded-lg ${inputBg} border ${cardBorder}`}
                      data-testid={`callback-${cb.call_id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 bg-orange-400/10 border border-orange-400/20">
                          <CalendarIcon size={12} className="text-orange-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-xs font-medium ${textPrimary} truncate`}>
                            {cb.contact_name || cb.phone_number || "Unknown"}
                          </p>
                          <p className={`text-[10px] ${textSecondary} truncate`}>
                            {cb.agent_name || "—"}
                          </p>
                        </div>
                      </div>
                      <span className={`text-[10px] ${textSecondary} font-mono shrink-0`}>{whenStr}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>

        {/* Chat Performance section removed iter139 — its content lives
            inside Performance Tracking above (Chat column). */}

        {/* Agent Summary Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 mb-3">
          {/* Voice Agents Card */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className={`${cardBg} backdrop-blur-xl rounded-xl border ${cardBorder} p-4`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center shadow-lg shadow-violet-400/20">
                  <Mic className="text-white" size={18} />
                </div>
                <div>
                  <p className={`text-xl font-bold ${textPrimary}`}>{stats?.voice_agents || 0}</p>
                  <p className={`text-xs ${textSecondary}`}>Voice Agents</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right mr-2">
                  <p className="text-base font-bold text-orange-400">{vBooked}</p>
                  <p className={`text-[10px] ${textSecondary}`}>Booked</p>
                </div>
                <Link to={`/workspace/${workspaceId}/sub-account/${subAccountId}/agents`}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-400/10 border border-violet-400/20 text-violet-400 text-xs font-medium hover:bg-violet-400/20 transition-colors">
                  View <ArrowUpRight size={12} />
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Chat Agents Card */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}
            className={`${cardBg} backdrop-blur-xl rounded-xl border ${cardBorder} p-4`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center shadow-lg shadow-teal-400/20">
                  <MessageSquare className="text-white" size={18} />
                </div>
                <div>
                  <p className={`text-xl font-bold ${textPrimary}`}>{stats?.chat_agents || 0}</p>
                  <p className={`text-xs ${textSecondary}`}>Chat Agents</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right mr-2">
                  <p className="text-base font-bold text-green-400">{cBooked}</p>
                  <p className={`text-[10px] ${textSecondary}`}>Booked</p>
                </div>
                <Link to={`/workspace/${workspaceId}/sub-account/${subAccountId}/agents`}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-400/10 border border-teal-400/20 text-teal-400 text-xs font-medium hover:bg-teal-400/20 transition-colors">
                  View <ArrowUpRight size={12} />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Transfer Analytics — only rendered when there were transfers in range */}
        {transferStats && transferStats.total_transfers > 0 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}
            className={`${cardBg} backdrop-blur-xl rounded-xl border ${cardBorder} p-5 mb-3`} data-testid="transfer-analytics-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400/20 to-orange-400/20 flex items-center justify-center">
                  <PhoneOutgoing className="text-amber-400" size={15} />
                </div>
                <div>
                  <h3 className={`text-base font-semibold ${textPrimary}`}>Transfer Analytics</h3>
                  <p className={`text-xs ${textSecondary}`}>How often your agents hand calls off — and to whom</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-base font-bold text-amber-400" data-testid="transfer-stats-total">{transferStats.total_transfers}</p>
                <p className={`text-[10px] ${textSecondary}`}>{transferStats.transfer_rate}% of {transferStats.total_calls} calls</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div>
                <p className={`text-[10px] font-medium ${textSecondary} uppercase tracking-wider mb-2.5`}>By Destination</p>
                <div className="space-y-2.5">
                  {transferStats.by_destination.slice(0, 5).map((d, di) => {
                    const pct = transferStats.total_transfers ? Math.round((d.count / transferStats.total_transfers) * 100) : 0;
                    return (
                      <div key={di} data-testid={`transfer-dest-stat-${di}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-medium ${textPrimary}`}>{d.destination}</span>
                          <span className={`text-xs ${textSecondary}`}>{d.count} ({pct}%)</span>
                        </div>
                        <div className={`h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`}>
                          <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-400" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className={`text-[10px] font-medium ${textSecondary} uppercase tracking-wider mb-2.5`}>By Agent</p>
                <div className="space-y-2.5">
                  {transferStats.by_agent.slice(0, 5).map((a, ai) => {
                    const pct = transferStats.total_transfers ? Math.round((a.count / transferStats.total_transfers) * 100) : 0;
                    return (
                      <div key={ai} data-testid={`transfer-agent-stat-${ai}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-medium ${textPrimary}`}>{a.agent}</span>
                          <span className={`text-xs ${textSecondary}`}>{a.count} ({pct}%)</span>
                        </div>
                        <div className={`h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`}>
                          <div className="h-full rounded-full bg-gradient-to-r from-violet-400 to-violet-500" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* AI Performance Insights */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className={`${cardBg} backdrop-blur-xl rounded-xl border ${cardBorder} p-5`} data-testid="ai-insights-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-400/20 to-teal-400/20 flex items-center justify-center">
                <Sparkles className="text-violet-400" size={16} />
              </div>
              <div>
                <h3 className={`text-base font-semibold ${textPrimary}`}>AI Performance Insights</h3>
                <p className={`text-xs ${textSecondary}`}>Powered by Claude — analysis of your agent metrics</p>
              </div>
            </div>
            <button
              onClick={fetchAiSummary}
              disabled={aiLoading}
              data-testid="generate-ai-insights-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                aiLoading
                  ? isDarkMode ? 'bg-white/[0.04] text-gray-500 cursor-wait' : 'bg-gray-100 text-gray-400 cursor-wait'
                  : 'bg-gradient-to-r from-violet-500 to-teal-500 text-white hover:shadow-lg hover:shadow-violet-400/20'
              }`}
            >
              {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {aiLoading ? 'Analyzing...' : aiSummary ? 'Refresh Insights' : 'Generate Insights'}
            </button>
          </div>

          {aiSummary ? (
            <div className={`p-4 rounded-xl ${inputBg} border ${cardBorder}`}>
              <div className={`text-sm leading-relaxed whitespace-pre-line ${textPrimary}`}>
                {aiSummary.summary}
              </div>
            </div>
          ) : (
            <div className={`flex items-center justify-center py-10 rounded-xl ${inputBg} border ${cardBorder}`}>
              <div className="text-center">
                <Sparkles className={`mx-auto mb-2 ${textSecondary}`} size={24} />
                <p className={`text-sm ${textPrimary} font-medium`}>Get AI-powered insights on your agent performance</p>
                <p className={`text-xs ${textSecondary} mt-1`}>Click "Generate Insights" to analyze which agents perform best and what could improve</p>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Reset Data Modal */}
      <AnimatePresence>
        {resetOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={(e) => { if (e.target === e.currentTarget) { setResetOpen(false); setResetConfirm(''); } }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              className={`w-full max-w-md rounded-2xl border shadow-2xl p-6 ${
                isDarkMode ? 'bg-[#0c0d12] border-white/[0.08]' : 'bg-white border-gray-200'
              }`}
              data-testid="reset-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                    <AlertTriangle className="text-red-400" size={20} />
                  </div>
                  <div>
                    <h3 className={`text-lg font-semibold ${textPrimary}`}>Reset All Data</h3>
                    <p className={`text-xs ${textSecondary}`}>This action cannot be undone</p>
                  </div>
                </div>
                <button onClick={() => { setResetOpen(false); setResetConfirm(''); }}
                  className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/[0.06]' : 'hover:bg-gray-100'}`}>
                  <X size={16} className={textSecondary} />
                </button>
              </div>

              <div className={`p-3 rounded-xl mb-4 ${isDarkMode ? 'bg-red-500/[0.06] border border-red-500/10' : 'bg-red-50 border border-red-100'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                  This will permanently delete all analytics events, chat sessions, and call records for this sub-account. Agent configurations will not be affected.
                </p>
              </div>

              <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>
                Type <span className={`font-mono font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>RESET</span> to confirm
              </label>
              <input
                type="text"
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                placeholder="Type RESET here"
                data-testid="reset-confirm-input"
                className={`w-full px-3 py-2.5 rounded-xl border text-sm mb-4 outline-none transition-colors ${
                  isDarkMode
                    ? 'bg-white/[0.04] border-white/[0.08] text-white placeholder-gray-600 focus:border-red-500/40'
                    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-red-300'
                }`}
              />

              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setResetOpen(false); setResetConfirm(''); }}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isDarkMode ? 'bg-white/[0.06] hover:bg-white/[0.1] text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  data-testid="reset-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetData}
                  disabled={resetConfirm !== 'RESET' || resetting}
                  data-testid="reset-confirm-btn"
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    resetConfirm === 'RESET'
                      ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20'
                      : isDarkMode ? 'bg-white/[0.04] text-gray-600 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {resetting ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                  {resetting ? 'Resetting...' : 'Reset Data'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {reportOpen && (
        <ReportModal
          api={api}
          subAccountId={subAccountId}
          agents={perf?.agents || []}
          isDarkMode={isDarkMode}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
};

const SubAccountDashboard = () => {
  return (
    <SubAccountLayout>
      <SubAccountDashboardContent />
    </SubAccountLayout>
  );
};

export default SubAccountDashboard;

